import { Opportunity } from "./opportunity";
import { Product } from "./product";
import { User } from "./user";


export enum ApprovalRequestType {
  PoV = 'pov',
  Escalation = 'escalation',
}

export enum ApprovalRequestSource {
  PoV = 'pov',
  Opportunity = 'opportunity',
  OpportunityProduct = 'opportunity_product',
}

export interface ApprovalRequestStatus {
  id: number;
  status: ApprovalRequestStatusLevel;
  title: string;
}

export enum ApprovalRequestStatusLevel {
  Pending = 1,
  UpdateRequired = 2,
  Approved = 3,
  Denied = 4,
  Cancelled = 5,
  Unassigned = 6,
  InProgress = 7,
  Completed = 8,
  Resolved = 9,
  Closed = 10,
  ReEscalated = 11,
  Superseded = 12,
}

export enum EscalationLevel {
  Domain = 1,
  Incubation = 2,
  Business = 3
}

export interface BusinessEscalationType {
  mapping_id: number;
  request_type_name: string;
}

export interface EscalationType {
  id: number;
  name: BusinessEscalationType['request_type_name'];
  description?: string;
}

export interface ApprovalRequest {
  id: number;
  title: string;
  requested_item_type: ApprovalRequestType;
  requested_item: Partial<Record<ApprovalRequestSource, any>> & { [key: string]: any }; // temporary until we sort out list payload
  justification?: string;
  approver_note?: string;
  created: string;
  last_updated: string;
  decision_timestamp?: string;
  approver: User;
  approver_email?: string;
  requestor: User;
  requestor_email?: string;
  status: ApprovalRequestStatus;
}

export type ApprovalRequestPayload = Partial<ApprovalRequest> & {
  id: ApprovalRequest['id'];
  requestor_id: User['id'];
  approver_id: User['id'];
  status_id: ApprovalRequestStatusLevel;
  requested_item_id: number;
}

export type RequestHistoryPayload = Partial<ApprovalRequestHistory> & {
  approval_request_id: ApprovalRequest['id'],
  action_by_user_id: User['id'],
  current_status_id: ApprovalRequestStatusLevel;
  previous_status_id?: ApprovalRequestStatusLevel | null;
};

export interface ApprovalRequestHistory {
  id: number;
  previous_justification?: string | null;
  current_justification?: string | null;
  previous_approver_note?: string | null;
  current_approver_note?: string | null;
  created: string;
  last_updated: string;
  approval_request: ApprovalRequest;
  previous_status?: number | null;
  current_status?: ApprovalRequestStatus;
  action_by_user: User['id'] | null;
}

export interface EscalationRequestAssignment {
  id: number;
  escalation: EscalationRequest;
  assigned_to: User;
  assignment_type: { id: number; name: string; level: number };
  status: ApprovalRequestStatus;
  created: string;
  last_updated: string;
  requested_by: User;
  product_support?: Product;
  notes?: string;
}

export interface EscalationRequestAssignmentPayload {
  escalation_id: EscalationRequest['id'],
  assigned_to_id: User['id'],
  assignment_type_id: number,
  status_id: ApprovalRequestStatusLevel,
  requested_by_id: User['id'],
  product_support_id?: EscalationSupportPath['id'],
  is_external?: boolean,
  external_system?: string,
  mapping_id?: BusinessEscalationType['mapping_id']
}

/**
 * One entry of a BU escalation ticket's worklog, as Jira returns it. Jira's own comment JSON is
 * camelCase; the backend stores it verbatim in `jira_comments`, so both shapes are accepted for
 * the author fields rather than assuming a normalization step that does not happen.
 */
export interface EscalationExternalTicketComment {
  id?: string | number;
  body?: string | null;
  created?: string | null;
  updated?: string | null;
  author?: {
    displayName?: string | null;
    emailAddress?: string | null;
    display_name?: string | null;
    email?: string | null;
    name?: string | null;
  } | null;
}

/**
 * A BU escalation's ticket in an external system (today always Jira).
 *
 * The Jira columns mirror whatever the last sync read from Jira, so they are the source of
 * truth for what the ticket's status and assignee actually are. Only the display subset the
 * escalation list returns is typed here; the API carries the full normalized payload too.
 */
export interface EscalationExternalTicket {
  id: number;
  escalation_id?: EscalationRequest['id'];
  assignment_id?: EscalationRequestAssignment['id'] | null;
  system?: string;
  /** Local sync state of the ticket row: 'created' | 'failed' | 'synced'. */
  status?: string;
  /** Which JSM service desk owns the request; needed to build the customer-portal link. */
  service_desk_id?: number | null;
  request_type_id?: number | null;
  request_type_name?: string | null;
  jira_issue_id?: string | null;
  jira_issue_key?: string | null;
  jira_issue_url?: string | null;
  jira_summary?: string | null;
  jira_status_name?: string | null;
  jira_status_category_key?: string | null;
  jira_status_category_color?: string | null;
  jira_request_current_status?: string | null;
  jira_resolution_name?: string | null;
  jira_resolution_at?: string | null;
  jira_assignee_display_name?: string | null;
  jira_assignee_email?: string | null;
  jira_assignee_username?: string | null;
  jira_customer_request_type?: string | null;
  jira_request_type_label?: string | null;
  jira_created_at?: string | null;
  jira_updated_at?: string | null;
  jira_latest_comment?: string | null;
  /** Only hydrated by the escalation detail lookup, not by the escalation list. */
  jira_comments?: EscalationExternalTicketComment[] | null;
  created?: string;
  last_updated?: string;
}

export interface EscalationRequest {
  id: number;
  opportunity: Opportunity,
  product: Product,
  product_name?: Product['name'];
  status: ApprovalRequestStatus;
  requested_by_id: User['id'],
  description: string,
  approvals?: EscalationApproval[];
  available_support_paths?: Array<EscalationSupportPath>;
  assignments?: Array<EscalationRequestAssignment>;
  stage: EscalationLevel;
  escalation_type?: EscalationType;
  escalation_type_name?: BusinessEscalationType['request_type_name'];
  jira_issue_id?: string;
  external_tickets?: Array<EscalationExternalTicket>;
  last_updated?: string;
}

export type EscalationRequestPayload = Partial<EscalationRequest> & {
  status_id: ApprovalRequestStatusLevel;
}

export interface EscalationSupportPath {
  id: number;
  assignment_type_id: number;
  assignment_type_name: string;
  assignment_type_level: EscalationLevel;
  group_members: EscalationSupportGroupMember[];
  bu_request_mappings?: BusinessEscalationType[];
}

export interface EscalationSupportGroupMember {
  id: User['id'];
  email: User['email'];
}

export interface EscalationApproval {
  id: number;
  escalation: EscalationRequest;
  level: EscalationLevel;
  target_assignment_type: { level: EscalationLevel, name: string };
  approval_request_id: ApprovalRequest['id'];
  approval_request?: ApprovalRequest;
  created: string;
  last_updated: string;
}

// APPROVAL TEMPLATE
export interface ApprovalTemplateFieldSection {
  type: 'field';
  modalOrder: number;
  id: string[];
  label: string;
}

export interface ApprovalTemplateGroupSection {
  type: 'group';
  modalOrder: number;
  label: string;
  fields: Array<{ id: string[]; label: string }>;
}

export interface ApprovalTemplateCollectionSection {
  type: 'collection';
  modalOrder: number;
  label: string;
  fields: Array<{ id: string[] }>;
}

export type ApprovalTemplateSection =
  | ApprovalTemplateFieldSection
  | ApprovalTemplateGroupSection
  | ApprovalTemplateCollectionSection;

export interface ApprovalTemplate {
  rootObjects: ApprovalRequestSource[];
  sections: { [section: string]: ApprovalTemplateSection[] };
}
